/**
 * src/components/spatial/spatialSurfaceValidation.ts
 *
 * What GeoLink will accept in a surface configuration, said in advance.
 *
 * GeoLink enforces these as database check constraints and refuses the
 * whole save with `invalid_configuration`, naming the constraint in a
 * `details` field the operator never sees. The result is a Save button
 * that appears to fail for no reason. Checking here means the operator
 * is told which box is wrong, in the words printed beside it, before a
 * round trip happens at all.
 *
 * The rules are GeoLink's, confirmed against a live instance:
 *
 *   max_activation_seconds  required, at least 1
 *   cooldown_seconds        required, zero or more
 *   width_meters            optional, but greater than zero if given
 *   height_meters           optional, but greater than zero if given
 *   orientation_degrees     optional, 0–360
 */

export interface SurfaceNumbers {
  maxActivationSeconds?: number | null;
  cooldownSeconds?: number | null;
  widthMeters?: number | null;
  heightMeters?: number | null;
  orientationDegrees?: number | null;
}

/** A measurement of zero is not a measurement; blank is how you say "unset". */
const DIMENSIONS: Array<[keyof SurfaceNumbers, string]> = [
  ['widthMeters', 'Width (m)'],
  ['heightMeters', 'Height (m)'],
];

/**
 * The first thing GeoLink would refuse, phrased for the operator, or
 * null if the configuration will be accepted.
 */
export function surfaceNumbersComplaint(surface: SurfaceNumbers): string | null {
  const max = surface.maxActivationSeconds;
  if (max == null || !Number.isFinite(Number(max)) || Number(max) < 1) {
    return 'Maximum activation (s) must be at least 1. Every activation has to expire, so it cannot be blank or zero.';
  }

  const cooldown = surface.cooldownSeconds;
  if (cooldown == null || !Number.isFinite(Number(cooldown)) || Number(cooldown) < 0) {
    return 'Cooldown (s) must be zero or more. Use 0 for no cooldown rather than leaving it blank.';
  }

  for (const [key, label] of DIMENSIONS) {
    const raw = surface[key];
    if (raw == null || raw === undefined) continue;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) {
      return `${label} must be greater than zero, or empty if the surface has no fixed size. Zero metres is not a measurement.`;
    }
  }

  const orientation = surface.orientationDegrees;
  if (orientation != null) {
    const value = Number(orientation);
    if (!Number.isFinite(value) || value < 0 || value > 360) {
      return 'Orientation (°) must be between 0 and 360, or empty if the direction does not matter.';
    }
  }

  return null;
}
